import { useMutation } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import {
  FieldTitle,
  required,
  useDataProvider,
  useInput,
  useIsMounted,
  useNotify,
  useTranslate,
  type Validator,
} from 'ra-core';
import { useDropzone } from 'react-dropzone';
import { type AdminDataProvider } from '../../api/adminDataProvider';
import { cn } from '../../lib/utils';
import { FormControl, FormError, FormField, FormLabel } from '../admin/form';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '../ui/input-group';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';

const urlValidation: Validator = (value: string) => {
  try {
    new URL(value);
    return undefined;
  } catch {
    return {
      message: 'moonstar.validation.invalidUrl',
      args: { _: 'Invalid URL' },
    };
  }
};

const validate = [urlValidation, required()];

type UploadMode = 'file' | 'website';

interface UrlSourceInputProps {
  source: string;
}

/**
 * A required URL input with an "Upload" button that opens a
 * dialog to upload a file, or a website (a `.zip` with an `index.html`), and fills the input with the uploaded URL.
 */
export function UrlSourceInput({ source }: UrlSourceInputProps) {
  const { id, field, isRequired } = useInput({ source, validate });

  const [uploadMode, setUploadMode] = useState<UploadMode>('file');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [droppedFile, setDroppedFile] = useState<File | null>(null);
  const [websiteUploadStatus, setWebsiteUploadStatus] = useState<ReactNode>();
  const isMounted = useIsMounted();

  const notify = useNotify();
  const translate = useTranslate();

  const handleClose = () => {
    setDialogOpen(false);
    setDroppedFile(null);
    setUploadMode('file');
  };

  const dataProvider = useDataProvider<AdminDataProvider>();

  const uploadFile = useMutation({
    // The file and mode are passed as variables: the mutation options are only refreshed in an effect, so reading
    // them from the closure could use stale state when Upload is clicked right after a file is picked.
    mutationFn: async ({ file, mode }: { file: File; mode: UploadMode }) => {
      if (mode === 'website') {
        const uploaded = await dataProvider.uploadWebsite(file, {
          onUploadProgress: (progress) => {
            if (!isMounted.current) return;

            const current = progress.uploadingIndex + 1;
            setWebsiteUploadStatus(
              translate('moonstar.upload.progress', {
                name: progress.uploadingFileName,
                current,
                total: progress.totalFiles,
                _: `Uploading ${progress.uploadingFileName} (${current}/${progress.totalFiles})...`,
              })
            );
          },
        });
        return uploaded;
      } else {
        const uploaded = await dataProvider.uploadFile(
          {
            rawFile: file,
            title: file.name,
          },
          'public'
        );

        return uploaded;
      }
    },
    onSuccess: (uploadedTo) => {
      setWebsiteUploadStatus(undefined);
      notify(
        translate('moonstar.upload.success', { _: 'Uploaded successfully!' }),
        { type: 'info' }
      );
      field.onChange(uploadedTo);
      handleClose();
    },
    onError: (err) => {
      setWebsiteUploadStatus(undefined);
      notify(
        translate('moonstar.upload.error', {
          error: err.message,
          _: `Failed to upload file: ${err.message}`,
        }),
        { type: 'error' }
      );
    },
  });

  const dropzone = useDropzone({
    multiple: false,
    accept:
      uploadMode === 'file'
        ? undefined
        : {
            'application/zip': ['.zip'],
          },
    onDrop(acceptedFiles) {
      if (acceptedFiles[0]) {
        setDroppedFile(acceptedFiles[0]);
      }
    },
    disabled: uploadFile.isPending,
  });

  return (
    <>
      <FormField id={id} name={field.name}>
        <FormLabel>
          <FieldTitle
            label={translate('moonstar.upload.contentUrl', {
              _: 'Content URL',
            })}
            source={source}
            isRequired={isRequired}
          />
        </FormLabel>
        <InputGroup>
          <FormControl>
            <InputGroupInput {...field} value={field.value ?? ''} />
          </FormControl>
          <InputGroupAddon align="inline-end">
            <InputGroupButton size="sm" onClick={() => setDialogOpen(true)}>
              {translate('moonstar.upload.upload', { _: 'Upload' })}
            </InputGroupButton>
          </InputGroupAddon>
        </InputGroup>
        <FormError />
      </FormField>
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) handleClose();
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {translate('moonstar.upload.upload', { _: 'Upload' })}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {translate('moonstar.upload.description', {
                _: 'Upload a file or a website to fill in the content URL',
              })}
            </DialogDescription>
          </DialogHeader>
          <Tabs
            value={uploadMode}
            onValueChange={(newValue) => {
              setDroppedFile(null);
              setUploadMode(newValue as UploadMode);
            }}
          >
            <TabsList className="w-full">
              <TabsTrigger value={'file' satisfies UploadMode}>
                {translate('moonstar.upload.file', { _: 'Upload file' })}
              </TabsTrigger>
              <TabsTrigger value={'website' satisfies UploadMode}>
                {translate('moonstar.upload.website', { _: 'Upload website' })}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <div
            {...dropzone.getRootProps({
              className: cn(
                'flex cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed p-8 text-center text-muted-foreground',
                dropzone.isDragActive && 'border-primary bg-primary/5'
              ),
            })}
          >
            <input {...dropzone.getInputProps()} />
            {droppedFile ? (
              <>
                <p className="text-lg font-medium break-all text-foreground">
                  {droppedFile.name}
                </p>
                {uploadMode === 'website' && websiteUploadStatus ? (
                  <p className="text-xs">{websiteUploadStatus}</p>
                ) : (
                  <p className="text-sm">
                    {translate('moonstar.upload.wrongFile', {
                      _: 'Wrong file?',
                    })}{' '}
                    <span className="font-medium text-primary uppercase">
                      {translate('moonstar.upload.uploadAgain', {
                        _: 'Upload again',
                      })}
                    </span>
                  </p>
                )}
              </>
            ) : (
              <>
                <p className="text-lg font-medium text-foreground">
                  {uploadMode === 'file'
                    ? translate('moonstar.upload.dropFile', {
                        _: 'Drag your file here',
                      })
                    : translate('moonstar.upload.dropWebsite', {
                        _: 'Drag your web content here',
                      })}
                </p>
                <p className="text-sm font-medium text-primary uppercase">
                  {translate('moonstar.upload.browse', { _: 'or browse' })}
                </p>
                {uploadMode === 'website' && (
                  <p className="text-xs">
                    {translate('moonstar.upload.websiteHint', {
                      _: "Select a .zip file. It should contain an 'index.html' file as the entry point",
                    })}
                  </p>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={handleClose}>
              {translate('ra.action.cancel', { _: 'Cancel' })}
            </Button>
            <Button
              type="button"
              disabled={!droppedFile || uploadFile.isPending}
              onClick={() => {
                if (droppedFile) {
                  uploadFile.mutate({ file: droppedFile, mode: uploadMode });
                }
              }}
            >
              {uploadFile.isPending && <Loader2 className="animate-spin" />}
              {translate('moonstar.upload.upload', { _: 'Upload' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
